import axios from "axios";
import Cookies from "js-cookie";
import storage from "@storage";
import { apiV2Url } from "config/environment";

// MC_DIRECT_MAILBOX_LOGIN is a shortcut for "log in, then press the webmail
// widget button": the login response carries the same webmail info the button
// fetches, and the browser goes there right away. The MC session is saved as
// usual (see completeLogin), so MC pages still open in another tab.

// Zimbra sends every user back with ?logout=true on logout, whether they came
// through a direct login or through the widget button. This marks the account
// whose session a direct login started, so only that logout ends the session.
export const DIRECT_LOGIN_KEY = "directLoginAccount";

// Kept on a direct login's logout - everything else, other accounts included,
// is cleared.
const PRESERVED_KEYS = [
  "deviceId",
  "notifications",
  "remembered",
  "lang",
  "version"
];

export const isDirectMailboxLogin = data =>
  parseInt(data?.options?.MC_DIRECT_MAILBOX_LOGIN, 10) === 1 &&
  !!data?.webmail?.url;

// Shared with the Zimbra/OWA widget button (layout/WidgetButtons/MailButton)
// so both open webmail the same way. The cookies are routing hints for the
// webmail proxy; exchange urls come without a scheme.
export const setWebmailCookies = ({ domain, server_id, mode } = {}, email) => {
  Cookies.set("s_domain", domain);
  Cookies.set("s_id", server_id);
  Cookies.set("s_schema", mode);
  Cookies.set("s_email", email);
};

export const getWebmailUrl = ({ url, mode } = {}, serverType) =>
  serverType === "exchange" ? mode + "://" + url : url;

export const redirectToWebmail = (email, data) => {
  storage.set(DIRECT_LOGIN_KEY, data.client_id || email);
  setWebmailCookies(data.webmail, email);
  window.location.href = getWebmailUrl(data.webmail, data.server_type);
};

// Runs before the store is created (see src/webmail-logout.js) so that after a
// direct login's logout the app boots with a clean storage: no auto login, no
// switching to another saved account. Any other ?logout=true (the widget
// button's Zimbra tab, or a session that already ended) is left alone - the app
// never read this parameter before, so the normal flow stays as it was.
export const handleWebmailLogoutRedirect = () => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("logout") !== "true") return;

  // Only while the direct login's account is the active one: with several
  // accounts the user may have switched to another and opened its webmail
  // through the widget button, which is the normal flow again.
  const directAccount = storage.get(DIRECT_LOGIN_KEY);
  if (!directAccount || storage.get("user")?.email !== directAccount) return;

  // Every saved account is cleared below, so every session it holds is
  // revoked too. A timed out account has no token left.
  const tokens = new Set(
    [
      storage.get("accessToken"),
      ...Object.values(storage.get("accounts") || {}).map(
        account => account?.access_token
      )
    ].filter(Boolean)
  );
  const deviceId = storage.get("deviceId");

  storage.clearWithout(PRESERVED_KEYS);

  tokens.forEach(token => {
    // Plain axios on purpose: axiosV2's interceptor turns a 401 into a session
    // timeout redirect and never settles auth errors.
    axios
      .delete(`${apiV2Url}/logout`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(deviceId ? { "Device-ID": deviceId } : {})
        }
      })
      .catch(() => {});
  });

  window.history.replaceState(null, "", "/login");
};
